import * as React from 'react'
import {ReactElement} from 'react'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import NatDisasterRepo from './repository/NatDisasterRepo'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import {PlainBackLink} from '../../../styledComponents/Links'
import {BlockSpacer} from '../../../styledComponents/ResponsiveConvenience'
import {Nullable} from '../../../app/TypeHelpers'
import {TopicInfo} from './repository/NatGetDisasterTopic'
import NatDisasterTopicArea from './resident/topic/NatDisasterTopicArea'
import moment, {Moment} from 'moment'
import {CommentInfo} from './repository/NatGetDisasterComment'
import {YES_OR_NOT} from '../../../CodeMaster'
import {NatPostDisasterCommentReadRequest} from './repository/NatPostDisasterCommentRead'
import NatDisasterCommentArea from './resident/comment/NatDisasterCommentArea'
import {Location} from 'react-router-dom'
import ReactDOM from 'react-dom'

interface Props {
    pastFlag?: boolean,
    condominiumCd: string,
    topicId: number
    disasterRepo: NatDisasterRepo,
    currentDateTimeRepo: NatCurrentDateTimeRepo,
    location: Location
}

interface State {
    topicInfo: Nullable<TopicInfo>
    comments: CommentInfo[]
    commentReadMap: Map<number, number>
    currentDateTime: Moment
    isLoadCompleted: boolean
}

class NatResidentDisasterTopicPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            topicInfo: null,
            comments: [],
            commentReadMap: new Map<number, number>(),
            currentDateTime: moment(),
            isLoadCompleted: false
        }
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            const [currentDateTime, topicInfo, comments] = await Promise.all([
                this.getCurrentDateTime(),
                this.getDisasterTopic(),
                this.getDisasterComments()
            ])

            if (currentDateTime && topicInfo && comments) {
                const involvementComments = comments.filter(commentInfo => {
                    // 主コメントが自身 or 返信コメントに自身が含まれるコメント取得
                    const main = commentInfo.isPosterSelf === YES_OR_NOT.YES.value
                    const reply = commentInfo.reply
                        .some(replyCommentInfo => replyCommentInfo.isPosterSelf === YES_OR_NOT.YES.value)
                    return main || reply
                })

                const mainCommentIds = comments
                    .filter(comment => comment.deleteDatetime === null)
                    .map(comment => comment.commentId)
                const involvementReplyCommentIds = involvementComments.flatMap(comment =>
                    comment.reply
                        .filter(reply => reply.deleteDatetime === null)
                        .map(reply => reply.commentId))

                // 主コメント、返信コメントに自身が含まれるコメントに対して既読
                const commentReadMap = await this.getDisasterCommentRead([
                    ...mainCommentIds,
                    ...involvementReplyCommentIds
                ])

                this.setState({
                    topicInfo: topicInfo,
                    comments: comments,
                    currentDateTime: moment(currentDateTime),
                    commentReadMap: commentReadMap
                })
            }
        } finally {
            this.setState({isLoadCompleted: true})
            eventEmitter.closeLoading()
        }
    }

    async getCurrentDateTime(): Promise<Nullable<string>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details?.currentDateTime ?? null
    }

    async getDisasterTopic(): Promise<Nullable<TopicInfo>> {
        const {pastFlag, condominiumCd, topicId, disasterRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            disasterRepo.getDisasterTopicDetail.bind(disasterRepo, condominiumCd, topicId),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )

        if (!pastFlag && response?.details.isPast === YES_OR_NOT.YES.value) {
            this.props.navigate(`/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}/past`,
                {replace: true}
            )
        }

        return response?.details ?? null
    }

    async getDisasterComments(): Promise<Nullable<CommentInfo[]>> {
        const {condominiumCd, topicId, disasterRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            disasterRepo.getDisasterComment.bind(disasterRepo, condominiumCd, topicId),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details?.commentInfo ?? null
    }

    async getDisasterCommentRead(commentIds: number[]): Promise<Map<number, number>> {
        const {pastFlag, condominiumCd, topicId, disasterRepo, eventEmitter} = this.props
        if (pastFlag) {
            // 過去トピックは既読付けない
            return new Map<number, number>()
        }

        const request: NatPostDisasterCommentReadRequest = {commentIds: commentIds}
        const response = await this.props.asyncCallFunction(
            disasterRepo.createDisasterCommentRead.bind(disasterRepo, condominiumCd, topicId, request),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )

        if (response?.details?.commentAlreadyInfo) {
            return new Map(response.details.commentAlreadyInfo.map(readInfo => {
                return [readInfo.commentId, readInfo.readCount]
            }))
        }
        return new Map<number, number>()
    }

    fetchReadComments = async (commentIds: number[]) => {
        await this.props.eventEmitter.showLoading()
        try {
            const commentReadMap = await this.getDisasterCommentRead(commentIds)
            this.setState({commentReadMap: new Map([...this.state.commentReadMap, ...commentReadMap])})
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    fetchComments = async () => {
        const comments = await this.getDisasterComments()
        if (comments) {
            ReactDOM.flushSync(() => {
                this.setState({comments: comments})
            })
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }
        const {
            pastFlag,
            condominiumCd,
            topicId,
            disasterRepo,
            navigate,
            location,
            eventEmitter,
            asyncCallFunction
        } = this.props
        const {topicInfo, currentDateTime, comments, commentReadMap} = this.state

        let topicListUrl = `/resident/condominiums/${condominiumCd}/disasters/topics`
        if (pastFlag) {
            topicListUrl += '/past'
        }

        return (
            <>
                <PlainBackLink
                    to={topicListUrl}>戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                {topicInfo &&
                    <NatDisasterTopicArea
                        pastFlag={pastFlag}
                        condominiumCd={condominiumCd}
                        topicInfo={topicInfo}
                        currentDateTime={currentDateTime}
                        navigate={navigate}
                        disasterRepo={disasterRepo}
                        location={location}
                        eventEmitter={eventEmitter}
                        asyncCallFunction={asyncCallFunction}
                    />
                }
                <NatDisasterCommentArea
                    pastFlag={pastFlag}
                    condominiumCd={condominiumCd}
                    topicId={topicId}
                    currentDateTime={currentDateTime}
                    comments={comments}
                    commentReadMap={commentReadMap}
                    onPostSuccess={this.fetchComments}
                    onReadComments={this.fetchReadComments}
                    disasterRepo={disasterRepo}
                    eventEmitter={eventEmitter}
                    asyncCallFunction={asyncCallFunction}
                />
                <BlockSpacer height={'15px'}/>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentDisasterTopicPage)